import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Link,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { groupBy } from "lodash";
import { useTranslation } from "react-i18next";
import truncate from "lodash/truncate";
import { SymbolV2 } from "@rentablo/api/apiv1";
import ArrowDownIcon from "@icons/myicons/arrow-down.svg?react";
import { SecurityImage } from "@rentablo/components";

const StyledTable = styled(Table)(({ theme }) => ({
  tableLayout: "fixed",
  "& p": {
    lineHeight: "16px",
  },

  "& .MuiTableHead-root:not(:first-of-type) .MuiTableCell-head": {
    paddingTop: theme.spacing(6),
  },

  "& .MuiTableCell-head": {
    border: "none !important",
  },

  "& .MuiTableCell-root": {
    padding: theme.spacing(1.5, 3),
    borderColor: theme.palette.border.secondary,
  },
  "& .MuiTableRow-root .MuiTableCell-root:first-of-type": {
    paddingLeft: theme.spacing(0),
  },
  "& .MuiTableRow-root .MuiTableCell-root:last-of-type": {
    paddingRight: theme.spacing(0),
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiTableHead-root:not(:first-of-type) .MuiTableCell-head": {
      paddingTop: theme.spacing(3),
    },

    "& .MuiTableHead-root:first-of-type .MuiTableCell-root": {
      padding: theme.spacing(0),
    },

    "& .MuiTableRow-head .MuiTableCell-root": {
      borderBottom: "none",
      paddingBottom: theme.spacing(0),
    },
  },
}));

const TypographyLink = styled(Typography)(({ theme }) => ({
  cursor: "pointer",
  color: theme.palette.primary.main,

  "&:hover": {
    textDecoration: "underline",
  },
}));

type Props = {
  symbols?: SymbolV2[];
  isFetching: boolean;
  onSymbolSelect: (symbol: SymbolV2) => void;
  logos?: Record<string, string | null | undefined>;
};

export const SecuritiesSearchResults = ({
  symbols,
  onSymbolSelect,
  isFetching,
  logos = {},
}: Props) => {
  const { t } = useTranslation();
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const [showMore, setShowMore] = useState<string | null>(null);
  const [rightPadding, setRightPadding] = useState(0);
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const groupedSymbols = useMemo(() => {
    const groups = groupBy(
      symbols?.filter((symbol) => symbol.type !== "index") || [],
      "type"
    );
    return showMore ? { [showMore]: groups[showMore] } : groups;
  }, [symbols, showMore]);

  useEffect(() => {
    if (!searchResultsRef?.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!searchResultsRef?.current) return;

      if (
        searchResultsRef.current.scrollHeight >
        searchResultsRef.current.offsetHeight
      ) {
        setRightPadding(3);
      }
    });
    resizeObserver.observe(searchResultsRef.current);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsRef.current]);

  if (isFetching || !symbols?.length) {
    return (
      <Paper
        sx={{
          width: "100%",
          marginTop: (theme) => theme.spacing(2.5),
          padding: (theme) => theme.spacing(4),
        }}
      >
        <Typography
          variant="body1"
          data-testid={isFetching ? "symbol-skeleton" : "no-results"}
        >
          {isFetching
            ? t("securitiesSearch.loading")
            : t("securitiesSearch.noResults")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        width: "100%",
        maxHeight: "calc(100vh - 400px)",
        marginTop: (theme) => theme.spacing(2.5),
        display: "flex",
      }}
    >
      <Box p={4} display="flex" flexDirection="column" gap={3} width="100%">
        {showMore && (
          <Box
            display="flex"
            alignItems="center"
            color="primary.main"
            onClick={() => setShowMore(null)}
            gap={1}
            sx={{
              cursor: "pointer",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              sx={{
                transform: "rotate(90deg)",
              }}
            >
              <ArrowDownIcon width="16px" height="16px" />
            </Box>
            <TypographyLink variant="caption">
              {t("securitiesSearch.back")}
            </TypographyLink>
          </Box>
        )}
        <Box
          display="flex"
          flexDirection="column"
          ref={searchResultsRef}
          gap={3}
          height="100%"
          pr={rightPadding}
          sx={{
            overflowY: "auto",
            position: "relative",
          }}
        >
          <Box>
            <StyledTable stickyHeader={Boolean(showMore)}>
              {Object.keys(groupedSymbols).map((group, index) => {
                const children = showMore
                  ? groupedSymbols[group]
                  : groupedSymbols[group]?.slice(0, 3);
                const isShowMoreDisplayed =
                  groupedSymbols[group]?.length > 3 && !showMore;

                return (
                  <React.Fragment key={group}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          style={{ maxWidth: "60%", width: "100%" }}
                          colSpan={isLessSm ? 3 : 1}
                        >
                          <Typography
                            variant="body1"
                            fontWeight={600}
                            mt={index !== 0 && !isShowMoreDisplayed ? 4 : 1}
                          >
                            {t(`investmentTypeClassification.types.${group}`)}
                          </Typography>
                        </TableCell>
                        {!isLessSm && (
                          <>
                            <TableCell
                              align="right"
                              style={{ maxWidth: "20%" }}
                            >
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                textAlign="right"
                              >
                                {t("securitiesSearch.isin")}
                              </Typography>
                            </TableCell>
                            <TableCell
                              align="right"
                              style={{ maxWidth: "20%" }}
                            >
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                textAlign="right"
                                textTransform="none"
                              >
                                {t("securitiesSearch.type")}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {children.map((symbol, index) => {
                        const image = logos[symbol.isin || symbol.id];

                        return (
                          <TableRow key={symbol.id}>
                            <TableCell
                              style={{ maxWidth: "60%", width: "100%" }}
                              colSpan={isLessSm ? 3 : 1}
                            >
                              <Box display="flex" alignItems="center" gap={2}>
                                <SecurityImage
                                  src={image || ""}
                                  alt={symbol.isin || symbol.id}
                                  width={24}
                                  height={24}
                                />
                                <Link onClick={() => onSymbolSelect(symbol)}>
                                  <Typography
                                    variant="caption"
                                    noWrap
                                    data-testid={`symbol-${index}`}
                                  >
                                    {truncate(symbol.name, { length: 50 })}
                                  </Typography>
                                </Link>
                              </Box>
                            </TableCell>
                            {!isLessSm && (
                              <>
                                <TableCell
                                  align="right"
                                  style={{ maxWidth: "20%" }}
                                >
                                  <Typography
                                    variant="caption"
                                    textAlign="right"
                                  >
                                    {symbol.isin ||
                                      symbol.id?.replace("_to_", " / ")}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  style={{ maxWidth: "20%" }}
                                >
                                  <Typography
                                    variant="caption"
                                    textAlign="right"
                                    sx={{
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {t(
                                      `investmentTypeClassification.types.${symbol.type}`
                                    )}
                                  </Typography>
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        );
                      })}

                      {isShowMoreDisplayed && (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            sx={{
                              borderBottom: "none",
                              borderTop: (theme) =>
                                `1px solid ${theme.palette.border.secondary}`,
                              paddingBottom: `0px !important`,
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              color="primary.main"
                              onClick={() => setShowMore(group)}
                              gap={1}
                              mt={2}
                              sx={{
                                cursor: "pointer",
                              }}
                            >
                              <TypographyLink variant="caption">
                                {t("securitiesSearch.showMore")}
                              </TypographyLink>
                              <Box
                                display="flex"
                                alignItems="center"
                                sx={{
                                  transform: "rotate(-90deg)",
                                }}
                              >
                                <ArrowDownIcon width="16px" height="16px" />
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </React.Fragment>
                );
              })}
            </StyledTable>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
